import React, { useCallback, useEffect, useState } from 'react';
import { Terminal } from 'lucide-react';
import { useAuth } from '../../../utils/AuthContext';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { DEFAULT_NOTIFICATION_PREFS, NotificationPrefs } from '../../../types';
import { NOTIFICATION_DEBUG_PATH } from '../../../utils/notificationDebugCatalog';
import ProfilePhotoSection from '../../ui/ProfilePhotoSection';
import UserProfileFields from '../UserProfileFields';
import NotificationSettingsForm from '../NotificationSettingsForm';
import ProjectNotificationPrefsList from '../ProjectNotificationPrefsList';
import SettingsSectionShell from '../SettingsSectionShell';
import { useSettingsSectionSave } from '../hooks/useSettingsSectionSave';
import '../NotificationSettingsForm.css';

const AccountSettingsSection: React.FC = () => {
  const { user } = useAuth();
  const { saving, saveStatus, runSave } = useSettingsSectionSave();
  const [prefs, setPrefs] = useState<NotificationPrefs>({ ...DEFAULT_NOTIFICATION_PREFS });
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (cancelled || !userDoc.exists()) return;
        const data = userDoc.data();
        if (data.notificationPrefs) {
          setPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...data.notificationPrefs });
        }
      } catch (err) {
        console.error('[AccountSettings] Load notifications error:', err);
      } finally {
        if (!cancelled) setLoadingPrefs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handlePrefsChange = useCallback((next: NotificationPrefs) => {
    setPrefs(next);
  }, []);

  const handleSaveNotifications = () => {
    if (!user?.id) return;
    runSave(async () => {
      const userRef = doc(db, 'users', user.id);
      const userDoc = await getDoc(userRef);
      const prev = (userDoc.exists() ? userDoc.data().notificationPrefs : null) as Partial<NotificationPrefs> | undefined;
      const next: NotificationPrefs = {
        ...DEFAULT_NOTIFICATION_PREFS,
        ...(prev || {}),
        ...prefs,
      };
      if (userDoc.exists()) {
        await updateDoc(userRef, { notificationPrefs: next });
      } else {
        await setDoc(userRef, { notificationPrefs: next }, { merge: true });
      }
    });
  };

  const isAdmin = user?.role === 'admin';

  return (
    <SettingsSectionShell
      title="My Account"
      description="Your profile, contact details, and how you receive notifications."
    >
      <section className="settings-ops-section" aria-labelledby="account-profile-heading">
        <h2 id="account-profile-heading" className="settings-ops-section-title">
          Profile
        </h2>
        <ProfilePhotoSection />
        <UserProfileFields />
      </section>

      <section className="settings-ops-section" aria-labelledby="account-notif-heading">
        <div className="settings-section-header" style={{ marginBottom: 12, paddingBottom: 0, border: 'none' }}>
          <div>
            <h2 id="account-notif-heading" className="settings-ops-section-title" style={{ marginBottom: 4 }}>
              Notifications
            </h2>
            <p className="settings-ops-hint">Personal preferences — what you receive, not what the team sees in Slack.</p>
          </div>
          <div className="settings-section-header-actions">
            {saveStatus === 'saved' && (
              <span className="settings-save-feedback settings-save-feedback--saved">Saved</span>
            )}
            <button
              type="button"
              className="pm-btn pm-btn-primary pm-btn-sm"
              onClick={handleSaveNotifications}
              disabled={saving || loadingPrefs}
            >
              {saving ? 'Saving…' : 'Save notifications'}
            </button>
          </div>
        </div>
        {loadingPrefs ? (
          <p className="settings-ops-hint">Loading preferences…</p>
        ) : (
          <NotificationSettingsForm
            prefs={prefs}
            onChange={handlePrefsChange}
            variant="internal"
            userEmail={user?.email}
          />
        )}
        <div style={{ marginTop: 20 }}>
          <h3 className="settings-ops-section-title" style={{ fontSize: 15, marginBottom: 8 }}>
            Notification settings by project
          </h3>
          <ProjectNotificationPrefsList />
        </div>
        {isAdmin && (
          <button
            type="button"
            className="pm-btn pm-btn-ghost pm-btn-sm"
            style={{ marginTop: 12, alignSelf: 'flex-start' }}
            onClick={() => window.open(NOTIFICATION_DEBUG_PATH, '_blank', 'noopener,noreferrer')}
          >
            <Terminal size={14} aria-hidden />
            Notification debug
          </button>
        )}
      </section>
    </SettingsSectionShell>
  );
};

export default AccountSettingsSection;
